import { Badge } from '@/components/ui/badge'
import SaveBar from '@/components/forms/SaveBar'
import { Skeleton } from '@/components/ui/skeleton'
import { Typography } from '@/components/ui/typography'
import { useAbility } from '@/lib/casl'
import { useAppForm } from '@/lib/form'
import {
  invalidateOrganizationBetaFeatureQueries,
  useOrganizationBetaFeatures,
  useUpdateOrganizationBetaFeatureMutation,
} from '@/lib/services/beta-features/beta-features.query'
import { cn, getErrorMessage } from '@/lib/utils'
import { toast } from 'sonner'

function betaFeatureFormDefaults(features) {
  return {
    enabled: Object.fromEntries(features.map((feature) => [feature.key, feature.enabled === true])),
  }
}

function FeatureField({ feature, form, isReadOnly, isSaving }) {
  return (
    <form.Subscribe selector={(state) => state.values.enabled?.[feature.key] === true}>
      {(checked) => {
        const isDirty = !feature.enabled_for_all && checked !== (feature.enabled === true)

        return (
          <div
            className={cn(
              'group flex items-center gap-3 rounded-xl border border-border bg-card px-3.5 py-3 text-left transition-[border-color,box-shadow] hover:border-primary/35 hover:shadow-sm',
              (isReadOnly || isSaving || feature.enabled_for_all) &&
                'hover:border-border/70 hover:bg-card hover:shadow-none'
            )}
          >
            <form.AppField name={`enabled.${feature.key}`}>
              {(field) => (
                <field.SwitchInput
                  className="[&>div]:items-start [&_[role=switch]]:mt-0.5"
                  label={
                    <span className="flex min-w-0 flex-wrap items-center gap-2">
                      <span className="font-semibold">{feature.name}</span>
                      {feature.enabled_for_all ? (
                        <Badge variant="success">Enabled for all</Badge>
                      ) : null}
                      {!feature.enabled_for_all && checked ? (
                        <Badge variant="outline">Enabled</Badge>
                      ) : null}
                      {isDirty ? <Badge variant="secondary">Unsaved</Badge> : null}
                    </span>
                  }
                  description={feature.description}
                  disabled={isReadOnly || isSaving || feature.enabled_for_all}
                  aria-label={`Enable ${feature.name} beta feature`}
                />
              )}
            </form.AppField>
          </div>
        )
      }}
    </form.Subscribe>
  )
}

function OrganizationBetaFeaturesForm({ features, isReadOnly, mutation, organizationId }) {
  const defaultValues = betaFeatureFormDefaults(features)
  const form = useAppForm({
    defaultValues,
    validators: {
      onSubmitAsync: async ({ value, formApi }) => {
        if (isReadOnly) return

        const changedFeatures = features.filter(
          (feature) =>
            !feature.enabled_for_all &&
            (value.enabled?.[feature.key] === true) !== (feature.enabled === true)
        )
        const updatedFeatureKeys = []
        let updateError = null

        for (const feature of changedFeatures) {
          try {
            await mutation.mutateAsync({
              organizationId,
              featureKey: feature.key,
              enabled: value.enabled?.[feature.key] === true,
            })
            updatedFeatureKeys.push(feature.key)
          } catch (error) {
            updateError = error
            break
          }
        }

        if (updatedFeatureKeys.length > 0) {
          try {
            await invalidateOrganizationBetaFeatureQueries({
              organizationId,
              featureKeys: updatedFeatureKeys,
            })
          } catch (error) {
            toast.error(getErrorMessage(error))
            return
          }
        }

        if (updateError) {
          const updatedFeatureKeySet = new Set(updatedFeatureKeys)
          const nextDefaultValues = betaFeatureFormDefaults(features)

          for (const featureKey of updatedFeatureKeys) {
            nextDefaultValues.enabled[featureKey] = value.enabled?.[featureKey] === true
          }

          formApi.reset(nextDefaultValues)

          for (const feature of changedFeatures) {
            if (!updatedFeatureKeySet.has(feature.key)) {
              formApi.setFieldValue(
                `enabled.${feature.key}`,
                value.enabled?.[feature.key] === true,
                { dontValidate: true }
              )
            }
          }

          toast.error(getErrorMessage(updateError))
          return
        }

        toast.success('Beta features updated.')
        setTimeout(() => formApi.reset(value))
      },
    },
  })
  const isSaving = mutation.isPending

  return (
    <form.AppForm>
      <form.Form className="space-y-2.5">
        {features.map((feature) => (
          <FeatureField
            key={feature.key}
            feature={feature}
            form={form}
            isReadOnly={isReadOnly}
            isSaving={isSaving}
          />
        ))}
        {!isReadOnly ? <SaveBar mutation={mutation} submitButtonText="Save changes" /> : null}
      </form.Form>
    </form.AppForm>
  )
}

export default function OrganizationBetaFeaturesSection({ organizationId }) {
  const ability = useAbility()
  const isReadOnly = !ability.can('access', 'global') || !ability.can('manage', 'beta-features')
  const featuresQuery = useOrganizationBetaFeatures(organizationId)
  const mutation = useUpdateOrganizationBetaFeatureMutation({ invalidateOnSuccess: false })
  const features = featuresQuery.data?.features ?? []

  return (
    <section className="rounded-xl border border-border bg-elevated p-5">
      <Typography variant="h3">Beta Features</Typography>
      <Typography variant="body" tone="muted" className="mt-1 block sm:text-sm/6">
        Choose which early-access features this florist can use.
      </Typography>

      <div className="mt-4 space-y-2.5">
        {featuresQuery.isPending ? (
          <>
            <Skeleton className="h-20 w-full rounded-xl bg-muted" />
            <Skeleton className="h-20 w-full rounded-xl bg-muted" />
          </>
        ) : featuresQuery.isError ? (
          <Typography variant="supporting">Could not load beta features.</Typography>
        ) : features.length > 0 ? (
          <OrganizationBetaFeaturesForm
            key={organizationId}
            features={features}
            isReadOnly={isReadOnly}
            mutation={mutation}
            organizationId={organizationId}
          />
        ) : (
          <Typography variant="supporting">
            No beta features are available for organizations yet.
          </Typography>
        )}
      </div>
    </section>
  )
}
